import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { AVATAR_LIST } from 'src/constants/queryKeys';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { calculateDiscountedPrice, generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { networkCreateAvatar, networkUpdateAvatar } from 'src/network/avatar';

const AvatarForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  const { appInfo } = useAppInfoStore();

  const { projectId } = useParams();

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const avatarDetails = modal?.data;
  const defaultValues = getDefaultValues(avatarDetails);
  const isEditMode = avatarDetails;

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    trigger,
    watch,
  } = methods;

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      AvatarApplicationID: +projectId,
      AvatarEnable: values?.AvatarEnable ? 1 : 0,
      AvatarAmountOriginal: +values?.AvatarAmountOriginal,
      AvatarDiscountPercent: +values?.AvatarDiscountPercent,
      AvatarAmountFinal: calculateDiscountedPrice(+values?.AvatarAmountOriginal, +values?.AvatarDiscountPercent),
      AvatarTransactionAmountTypeID: selectedAmountType,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateAvatar(avatarDetails?.AvatarID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آواتار موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries([AVATAR_LIST]);
          } else {
            enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
          }
        }
      } else {
        const res = await networkCreateAvatar(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آواتار موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries([AVATAR_LIST]);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');
    setAmountTypes(amountTypeList);
    setSelectedAmountType(avatarDetails?.AvatarTransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  const amountOrginal = watch('AvatarAmountOriginal');
  const discountPercent = watch('AvatarDiscountPercent');

  useEffect(() => {
    if (amountOrginal && discountPercent !== undefined) {
      const finalAmount = calculateDiscountedPrice(parseFloat(amountOrginal), parseFloat(discountPercent));
      setValue('AvatarAmountFinal', finalAmount?.toLocaleString());
    }
  }, [amountOrginal, discountPercent, setValue]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="AvatarTitle" type="text" label="عنوان آواتار" />

              <GeneralSelectMenu
                label="واحد پرداخت"
                name="AvatarTransactionAmountTypeID"
                options={amountTypes}
                value={selectedAmountType}
                onChangeValue={setSelectedAmountType}
              />

              {/* <GeneralUpload
                label="تصویر پیشنمایش"
                id="preview_image"
                name="AvatarURLPreview"
                afterChangeAction={(res) => {
                  setValue('AvatarURLPreview', res);
                  trigger('AvatarURLPreview');
                }}
              /> */}

              <GeneralUpload
                label="تصویر اصلی"
                id="original_image"
                name="AvatarURLOriginal"
                afterChangeAction={(res) => {
                  setValue('AvatarURLOriginal', res);
                  trigger('AvatarURLOriginal');
                }}
              />

              <RHFTextField name="AvatarAmountOriginal" type="text" label="ارزش اولیه آواتار" />

              <RHFTextField name="AvatarDiscountPercent" type="text" label="درصد تخفیف" />

              <RHFTextField name="AvatarAmountFinal" type="text" label="هزینه نهایی (پس از تخفیف)" readOnly={true} />

              <RHFSwitch name="AvatarEnable" labelPlacement="end" label="نمایش داده شود؟" />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default AvatarForm;
